import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for session-based analytics tracking
 * Ensures events are only logged once per component mount/session
 * 
 * @param logFunction - The analytics logging function to call
 * @param dependencies - Array of dependencies that should trigger the event
 * @param enabled - Optional boolean to control if analytics should fire (default: true)
 */
export const useAnalytics = (
  logFunction: () => void,
  dependencies: any[],
  enabled: boolean = true
) => {
  const hasLoggedAnalytics = useRef(false);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    // Only log if:
    // 1. Analytics haven't been logged for this session
    // 2. User is authenticated
    // 3. Enabled flag is true
    // 4. User data is available
    if (!hasLoggedAnalytics.current && isAuthenticated && enabled && user) {
      logFunction();
      hasLoggedAnalytics.current = true;
    }
  }, [isAuthenticated, enabled, user, ...dependencies]);

  // Reset the flag when component unmounts to allow fresh logging on next mount
  useEffect(() => {
    return () => {
      hasLoggedAnalytics.current = false;
    };
  }, []);
};